import SchoolIcon from "@mui/icons-material/School";
import {
  Alert,
  Box,
  Button,
  Grid,
  Snackbar,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FunctionComponent,
  type MouseEvent as ReactMouseEvent,
  type SyntheticEvent,
} from "react";
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  getConnectedEdges,
  type Connection,
  type Edge,
  type Node,
  type NodeMouseHandler,
  type OnConnect,
  type OnEdgesChange,
  type OnInit,
  type OnNodesChange,
  type OnSelectionChangeParams,
  type ReactFlowInstance,
  type XYPosition,
} from "reactflow";
import { THREE_COLUMN_MIN_WIDTH, TITLE_HEIGHT } from "../constants";
import type DifferentiationMode from "../core/DifferentiationMode";
import Operation from "../core/Operation";
import Port from "../core/Port";
import type AddNodeData from "../features/AddNodeData";
import {
  ADD_DFDX_CODE,
  ADD_F_CODE,
  BINARY_CROSS_ENTROPY_DFDX_CODE,
  BINARY_CROSS_ENTROPY_F_CODE,
  COS_DFDX_CODE,
  COS_F_CODE,
  DIVIDE_DFDX_CODE,
  DIVIDE_F_CODE,
  EXP_DFDX_CODE,
  EXP_F_CODE,
  IDENTITY_DFDX_CODE,
  IDENTITY_F_CODE,
  INC_DFDX_CODE,
  INC_F_CODE,
  LN_DFDX_CODE,
  LN_F_CODE,
  MULTIPLY_DFDX_CODE,
  MULTIPLY_F_CODE,
  POWER_DFDX_CODE,
  POWER_F_CODE,
  PRODUCT_DFDX_CODE,
  PRODUCT_F_CODE,
  RELU_DFDX_CODE,
  RELU_F_CODE,
  SIGMOID_DFDX_CODE,
  SIGMOID_F_CODE,
  SIN_DFDX_CODE,
  SIN_F_CODE,
  SQUARED_ERROR_DFDX_CODE,
  SQUARED_ERROR_F_CODE,
  SUBTRACT_DFDX_CODE,
  SUBTRACT_F_CODE,
  SUM_DFDX_CODE,
  SUM_F_CODE,
  TANH_DFDX_CODE,
  TANH_F_CODE,
  TAN_DFDX_CODE,
  TAN_F_CODE,
  TEMPLATE_DFDX_CODE,
  TEMPLATE_F_CODE,
} from "../features/BuiltInCode";
import CoreGraphAdapter from "../features/CoreGraphAdapter";
import type ExecutionStep from "../features/ExecutionStep";
import type ExplainDerivativeData from "../features/ExplainDerivativeData";
import type InspectorTab from "../features/InspectorTab";
import type FeatureNodeType from "../features/FeatureNodeType";
import type FeatureOperation from "../features/FeatureOperation";
import { findFeatureOperation } from "../features/FeatureOperationFinder";
import {
  exportGraphImage,
  type ExportImageType,
} from "../features/GraphExporter";
import { layoutGraph } from "../features/GraphLayout";
import {
  emptyManualDerivativePlan,
  findBlockingNodeIds,
  findReadyNodeIds,
} from "../features/ManualDerivativeController";
import type ManualDerivativePlan from "../features/ManualDerivativePlan";
import type NodeData from "../features/NodeData";
import NodeNameBuilder from "../features/NodeNameBuilder";
import { idleWalkthroughState } from "../features/NodeWalkthroughState";
import type { DecimalPlaces } from "../features/NumberFormatter";
import {
  addEdgeMarkers,
  addReactFlowNode,
  buildEdgeMarker,
  clearStepEdgeHighlights,
  deselectAllNodes,
  findFirstSelectedNodeId,
  getNewReactFlowNodePosition,
  hideInputField,
  highlightStepEdges,
  revealStepOutput,
  selectAllNodes,
  selectReactFlowNode,
  setCollapsedOnNodes,
  setManualModeOnNodes,
  setStepModeOnNodes,
  showInputFields,
  toggleNodeCollapse,
  updateEdgeAnimations,
  updateEdgeLabels,
  updateManualNodeStates,
  updateReactFlowNodeDarkMode,
  updateReactFlowNodeDecimalPlaces,
  updateReactFlowNodeDerivatives,
  updateReactFlowNodeFValues,
  updateReactFlowNodeHighlighted,
  updateReactFlowNodeInputValue,
  updateReactFlowNodeName,
  updateReactFlowNodeStepStates,
  updateReactFlowNodeTopologicalOrder,
  updateReactFlowNodeValuesVisibility,
  type StepFlowDirection,
} from "../features/ReactFlowController";
import type SelectedFeature from "../features/SelectedFeature";
import ReactFlowGraph from "../reactflow/ReactFlowGraph";
import { gridGap } from "../reactflow/styles";
import ReactFlowGraphTestHelper from "../reactflow/ReactFlowGraphTestHelper";
import type CoreOperationState from "../states/CoreOperationState";
import type FeatureOperationState from "../states/FeatureOperationState";
import type GraphContainerState from "../states/GraphContainerState";
import type PortState from "../states/PortState";
import ExplainDerivativesPanel from "./ExplainDerivativesPanel";
import FeaturePanel from "./FeaturePanel";
import GraphToolbar from "./GraphToolbar";
import ManualDerivativePanel from "./ManualDerivativePanel";
import RightPanelDock, { type RightPanelTab } from "./RightPanelDock";
import NodeContextMenu, { type NodeContextMenuAnchor } from "./NodeContextMenu";
import PaneContextMenu, { type PaneContextMenuAnchor } from "./PaneContextMenu";
import SettingsMenu from "./SettingsMenu";
import StepExecutionPanel from "./StepExecutionPanel";
import Title from "./Title";

const isTest = process.env.NODE_ENV === "test";

interface GraphContainerProps {
  // Sidebar
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  // Feature
  selectedFeature: SelectedFeature | null;
  onSelectFeature: (feature: SelectedFeature | null) => void;
  // Theme
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  // Tooltips
  isTooltipsVisible: boolean;
  onToggleTooltips: () => void;
}

const GraphContainer: FunctionComponent<GraphContainerProps> = ({
  isSidebarOpen,
  onToggleSidebar,
  selectedFeature,
  onSelectFeature,
  isDarkMode,
  onToggleDarkMode,
  isTooltipsVisible,
  onToggleTooltips,
}) => {
  const theme = useTheme();

  // Core graph
  const [coreGraphAdapter] = useState<CoreGraphAdapter>(new CoreGraphAdapter());

  // Graph states
  const [isReverseMode, setReverseMode] = useState<boolean>(true);
  const [derivativeTarget, setDerivativeTarget] = useState<string | null>(null);
  const [featureOperations, setFeatureOperations] = useState<
    FeatureOperation[]
  >([
    {
      id: "add",
      name: "Add",
      type: "basic",
      namePrefix: "a",
      operation: new Operation(ADD_F_CODE, ADD_DFDX_CODE),
      inputPorts: [new Port("a", false), new Port("b", false)],
      helpText: "Add two numbers, i.e., a + b",
    },
    {
      id: "inc",
      name: "Inc",
      type: "basic",
      namePrefix: "i",
      operation: new Operation(INC_F_CODE, INC_DFDX_CODE),
      inputPorts: [new Port("x", false)],
      helpText: "Increment a number by 1, i.e., x + 1",
    },
    {
      id: "subtract",
      name: "Subtract",
      type: "basic",
      namePrefix: "s",
      operation: new Operation(SUBTRACT_F_CODE, SUBTRACT_DFDX_CODE),
      inputPorts: [new Port("a", false), new Port("b", false)],
      helpText: "Subtract two numbers, i.e., a - b",
    },
    {
      id: "multiply",
      name: "Multiply",
      type: "basic",
      namePrefix: "m",
      operation: new Operation(MULTIPLY_F_CODE, MULTIPLY_DFDX_CODE),
      inputPorts: [new Port("a", false), new Port("b", false)],
      helpText: "Multiply two numbers, i.e., a * b",
    },
    {
      id: "divide",
      name: "Divide",
      type: "basic",
      namePrefix: "d",
      operation: new Operation(DIVIDE_F_CODE, DIVIDE_DFDX_CODE),
      inputPorts: [new Port("a", false), new Port("b", false)],
      helpText: "Divide two numbers, i.e., a / b",
    },
    {
      id: "power",
      name: "Power",
      type: "basic",
      namePrefix: "p",
      operation: new Operation(POWER_F_CODE, POWER_DFDX_CODE),
      inputPorts: [new Port("x", false), new Port("n", false)],
      helpText: "Calculate the power, i.e., x^n",
    },
    {
      id: "exp",
      name: "Exp",
      type: "basic",
      namePrefix: "e",
      operation: new Operation(EXP_F_CODE, EXP_DFDX_CODE),
      inputPorts: [new Port("x", false)],
      helpText: "Calculate the exponential function, i.e., e^x",
    },
    {
      id: "ln",
      name: "Ln",
      type: "basic",
      namePrefix: "l",
      operation: new Operation(LN_F_CODE, LN_DFDX_CODE),
      inputPorts: [new Port("x", false)],
      helpText: "Calculate the log: ln(x)",
    },
    {
      id: "sum",
      name: "Sum",
      type: "aggregate",
      namePrefix: "s",
      operation: new Operation(SUM_F_CODE, SUM_DFDX_CODE),
      inputPorts: [new Port("x_i", true)],
      helpText: "Add all inputs: sum_i x_i, i.e., x_0 + x_1 + ...",
    },
    {
      id: "product",
      name: "Product",
      type: "aggregate",
      namePrefix: "p",
      operation: new Operation(PRODUCT_F_CODE, PRODUCT_DFDX_CODE),
      inputPorts: [new Port("x_i", true)],
      helpText: "Multiply all inputs: prod_i x_i, i.e., x_0 * x_1 * ...",
    },
    {
      id: "sin",
      name: "Sin",
      type: "trigonometric",
      namePrefix: "s",
      operation: new Operation(SIN_F_CODE, SIN_DFDX_CODE),
      inputPorts: [new Port("x", false)],
      helpText: "Calculate sin(x)",
    },
    {
      id: "cos",
      name: "Cos",
      type: "trigonometric",
      namePrefix: "c",
      operation: new Operation(COS_F_CODE, COS_DFDX_CODE),
      inputPorts: [new Port("x", false)],
      helpText: "Calculate cos(x)",
    },
    {
      id: "tan",
      name: "Tan",
      type: "trigonometric",
      namePrefix: "t",
      operation: new Operation(TAN_F_CODE, TAN_DFDX_CODE),
      inputPorts: [new Port("x", false)],
      helpText: "Calculate tan(x)",
    },
    {
      id: "linear",
      name: "Linear",
      type: "activation",
      namePrefix: "l",
      operation: new Operation(IDENTITY_F_CODE, IDENTITY_DFDX_CODE),
      inputPorts: [new Port("x", false)],
      helpText: "Linear activation function, i.e., y=x",
    },
    {
      id: "sigmoid",
      name: "Sigmoid",
      type: "activation",
      namePrefix: "s",
      operation: new Operation(SIGMOID_F_CODE, SIGMOID_DFDX_CODE),
      inputPorts: [new Port("x", false)],
      helpText: "Sigmoid activation function, i.e., y=1/(1 + e^(-x))",
    },
    {
      id: "tanh",
      name: "Tanh",
      type: "activation",
      namePrefix: "s",
      operation: new Operation(TANH_F_CODE, TANH_DFDX_CODE),
      inputPorts: [new Port("x", false)],
      helpText:
        "Tanh activation function, i.e., y=(e^x - e^(-x))(e^x + e^(-x))",
    },
    {
      id: "relu",
      name: "ReLU",
      type: "activation",
      namePrefix: "r",
      operation: new Operation(RELU_F_CODE, RELU_DFDX_CODE),
      inputPorts: [new Port("x", false)],
      helpText: "ReLU activation function, i.e., y=max(0, x)",
    },
    {
      id: "squared_error",
      name: "Squared Error",
      type: "loss",
      namePrefix: "s",
      operation: new Operation(SQUARED_ERROR_F_CODE, SQUARED_ERROR_DFDX_CODE),
      inputPorts: [new Port("y_t", false), new Port("y_e", false)],
      helpText: `Calculate the squared error, i.e., (y_t - y_e)^2
 (y_t: true value, y_e: estimated value)`,
    },
    {
      id: "binary_cross_entropy",
      name: "Binary Cross-Entropy",
      type: "loss",
      namePrefix: "b",
      operation: new Operation(
        BINARY_CROSS_ENTROPY_F_CODE,
        BINARY_CROSS_ENTROPY_DFDX_CODE,
      ),
      inputPorts: [new Port("y_t", false), new Port("y_e", false)],
      helpText: `Calculate the binary cross-entropy,
 i.e., y_t * log(y_e) + (1 - y_t) * log(1 - y_e)
 (y_t: true value, y_e: estimated value)`,
    },
  ]);
  const [nextNodeId, setNextNodeId] = useState<number>(0);
  const [nodeNameBuilder] = useState<NodeNameBuilder>(new NodeNameBuilder());
  const [nextOperationId, setNextOperationId] = useState<number>(0);

  // Feature panel states
  const [operationIdsAddedAtLeastOnce, setOperationIdsAddedAtLeastOnce] =
    useState(new Set<string>());
  const [explainDerivativeData, setExplainDerivativeData] = useState<
    ExplainDerivativeData[]
  >([]);

  // React Flow states
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);
  const [reactFlowNodes, setReactFlowNodes] = useState<Node[]>([]);
  const [reactFlowEdges, setReactFlowEdges] = useState<Edge[]>([]);
  const [lastSelectedNodeId, setLastSelectedNodeId] = useState<string | null>(
    null,
  );

  // The derivatives inspector. Which tab is showing is what decides whether a
  // walkthrough is running, so there is no separate flag per mode to keep in
  // step with the panel that drives it
  const [isInspectorOpen, setInspectorOpen] = useState<boolean>(false);
  // Explain is the tab to land on: it is the only one that does not hold the
  // outputs back, so opening the inspector never blanks the graph out
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>("explain");

  // Step-by-step execution states
  const [stepPlan, setStepPlan] = useState<ExecutionStep[]>([]);
  // Number of steps already revealed, from 0 to stepPlan.length
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [isPlaying, setPlaying] = useState<boolean>(false);

  const isStepMode = isInspectorOpen && inspectorTab === "step";
  const isManualMode = isInspectorOpen && inspectorTab === "manual";
  const isExplaining = isInspectorOpen && inspectorTab === "explain";

  // Manual derivative states
  const [manualPlan, setManualPlan] = useState<ManualDerivativePlan>(
    emptyManualDerivativePlan,
  );
  const [manualComputedNodeIds, setManualComputedNodeIds] = useState<
    Set<string>
  >(new Set());
  // The node whose chain rule the panel is showing, which is the last one
  // clicked rather than the last one computed
  const [manualSelectedNodeId, setManualSelectedNodeId] = useState<
    string | null
  >(null);
  const [manualExplainData, setManualExplainData] =
    useState<ExplainDerivativeData | null>(null);

  // Canvas settings. They're view preferences like the dark mode, so they're
  // deliberately left out of the saved state
  const [isGridVisible, setGridVisible] = useState<boolean>(false);
  const [isSnapToGrid, setSnapToGrid] = useState<boolean>(false);
  const [decimalPlaces, setDecimalPlaces] = useState<DecimalPlaces>(2);
  const [isNodeValuesVisible, setNodeValuesVisible] = useState<boolean>(true);
  const [isNodeDerivativesVisible, setNodeDerivativesVisible] =
    useState<boolean>(true);
  const [isLocalDerivativesVisible, setLocalDerivativesVisible] =
    useState<boolean>(false);
  const [isTopologicalOrderVisible, setTopologicalOrderVisible] =
    useState<boolean>(false);
  const [isGlobalNodeInfluence, setGlobalNodeInfluence] =
    useState<boolean>(false);
  // Whether the manual run shows the values it has worked out. Off, the run
  // still records them, so the same graph can be gone over again without the
  // progress being given up
  const [areAnswersShown, setAnswersShown] = useState<boolean>(true);
  const [isExporting, setExporting] = useState<boolean>(false);

  // Right-click menu on the canvas
  const [paneContextMenuAnchor, setPaneContextMenuAnchor] =
    useState<PaneContextMenuAnchor | null>(null);

  // Right-click menu on a node
  const [contextMenuAnchor, setContextMenuAnchor] =
    useState<NodeContextMenuAnchor | null>(null);

  // Error messages
  const [isSnackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Bumped to ask the canvas to fit the graph into its new width
  const [fitRequestId, setFitRequestId] = useState<number>(0);

  // Whether the window can hold the feature panel and the right column at the
  // same time without squeezing the canvas. Assumed true where there's no way
  // to ask, so that the panel is never taken away on a guess
  const isWideEnoughForBothPanels = useMediaQuery(
    `(min-width:${THREE_COLUMN_MIN_WIDTH}px)`,
    { defaultMatches: true },
  );
  // The feature the right column borrowed the room from, to hand back later
  const yieldedFeatureRef = useRef<SelectedFeature | null>(null);
  const wasRightColumnOpenRef = useRef<boolean>(false);

  /**
   * Rebuilds the whole step-by-step visual state for the given step index.
   *
   * Everything is recomputed from scratch instead of being applied
   * incrementally, which makes stepping backward, resetting and jumping around
   * behave identically, and lets nodes added mid-run join the next step.
   *
   * @param plan The step plan to walk through.
   * @param newStepIndex How many steps should be revealed.
   */
  const applyStepIndex = useCallback(
    (plan: ExecutionStep[], newStepIndex: number) => {
      const boundedIndex = Math.max(0, Math.min(newStepIndex, plan.length));
      const activeStep =
        boundedIndex > 0 ? plan[boundedIndex - 1] ?? null : null;

      setReactFlowNodes((nodes) => {
        // Hide every output, then reveal the ones the run has reached. The
        // underlying values are always up to date because the core graph is
        // fully evaluated, so nothing needs recomputing here
        let newNodes = setStepModeOnNodes(true, nodes);
        const visitedNodeIds = new Set<string>();
        for (let i = 0; i < boundedIndex; i++) {
          const step = plan[i];
          newNodes = revealStepOutput(step.nodeId, step.phase, newNodes);
          visitedNodeIds.add(step.nodeId);
        }
        return updateReactFlowNodeStepStates(
          activeStep === null ? null : activeStep.nodeId,
          activeStep === null ? null : activeStep.phase,
          visitedNodeIds,
          newNodes,
        );
      });

      setReactFlowEdges((edges) => {
        if (activeStep === null) {
          return clearStepEdgeHighlights(edges);
        }
        // The value always flows from the inputs. The gradient flows from the
        // outputs in reverse mode and from the inputs in forward mode
        const direction: StepFlowDirection =
          activeStep.phase === "value" || !isReverseMode
            ? "incoming"
            : "outgoing";
        // f() needs every input, but the chain rule only sums over neighbors
        // on the differentiation path
        const flowNodeIds =
          activeStep.phase === "value"
            ? null
            : new Set(coreGraphAdapter.getDerivativePathNodeIds());
        return highlightStepEdges(
          activeStep.nodeId,
          direction,
          activeStep.phase,
          flowNodeIds,
          edges,
        );
      });

      setStepIndex(boundedIndex);
    },
    [coreGraphAdapter, isReverseMode],
  );

  const handleStepForward = useCallback(() => {
    applyStepIndex(stepPlan, stepIndex + 1);
  }, [applyStepIndex, stepIndex, stepPlan]);

  const handleStepBackward = useCallback(() => {
    setPlaying(false);
    applyStepIndex(stepPlan, stepIndex - 1);
  }, [applyStepIndex, stepIndex, stepPlan]);

  const handleResetSteps = useCallback(() => {
    setPlaying(false);
    applyStepIndex(stepPlan, 0);
  }, [applyStepIndex, stepPlan]);

  const handleTogglePlay = useCallback(() => {
    setPlaying((isPlaying) => !isPlaying);
  }, []);

  const handleToggleGrid = useCallback(() => {
    setGridVisible((isGridVisible) => !isGridVisible);
  }, []);

  const handleToggleSnapToGrid = useCallback(() => {
    setSnapToGrid((isSnapToGrid) => !isSnapToGrid);
  }, []);

  const handleToggleAnswers = useCallback(() => {
    setAnswersShown((areAnswersShown) => !areAnswersShown);
  }, []);

  const handleToggleNodeValues = useCallback(() => {
    setNodeValuesVisible((isNodeValuesVisible) => !isNodeValuesVisible);
  }, []);

  const handleToggleNodeDerivatives = useCallback(() => {
    setNodeDerivativesVisible(
      (isNodeDerivativesVisible) => !isNodeDerivativesVisible,
    );
  }, []);

  const handleToggleLocalDerivatives = useCallback(() => {
    setLocalDerivativesVisible(
      (isLocalDerivativesVisible) => !isLocalDerivativesVisible,
    );
  }, []);

  const handleToggleTopologicalOrder = useCallback(() => {
    setTopologicalOrderVisible((isTopologicalOrderVisible) => {
      const next = !isTopologicalOrderVisible;
      const topoMap = coreGraphAdapter.getTopologicalOrderMap();
      setReactFlowNodes((nodes) =>
        updateReactFlowNodeTopologicalOrder(topoMap, next, nodes),
      );
      return next;
    });
  }, [coreGraphAdapter]);

  const handleToggleGlobalNodeInfluence = useCallback(() => {
    setGlobalNodeInfluence((isGlobalNodeInfluence) => {
      const next = !isGlobalNodeInfluence;
      if (!isStepMode && !isManualMode) {
        const selectedNodeIds = reactFlowNodes
          .filter((node) => node.selected)
          .map((node) => node.id);
        setReactFlowEdges((edges) =>
          updateEdgeAnimations(
            isExplaining,
            isReverseMode,
            derivativeTarget !== null,
            selectedNodeIds,
            new Set(coreGraphAdapter.getDerivativePathNodeIds()),
            edges,
            next,
          ),
        );
      }
      return next;
    });
  }, [
    coreGraphAdapter,
    derivativeTarget,
    isExplaining,
    isManualMode,
    isReverseMode,
    isStepMode,
    reactFlowNodes,
  ]);

  const handleDecimalPlacesChange = useCallback(
    (decimalPlaces: DecimalPlaces) => {
      setDecimalPlaces(decimalPlaces);
    },
    [],
  );

  /**
   * Writes the graph to an image file.
   *
   * Rendering the canvas to an image takes a moment on a big graph, so the menu
   * items are disabled while it runs to keep a second export from piling on top
   * of the first.
   */
  const handleExport = useCallback(
    (imageType: ExportImageType) => {
      setExporting(true);
      const nodesToExport = reactFlowInstance?.getNodes() ?? reactFlowNodes;
      exportGraphImage(
        imageType,
        nodesToExport,
        theme.palette.background.default,
      )
        .catch((error: unknown) => {
          const message =
            error instanceof Error ? error.message : "Unknown error";
          setErrorMessage(`Could not export the graph: ${message}`);
          setSnackbarOpen(true);
        })
        .finally(() => {
          setExporting(false);
        });
    },
    [reactFlowInstance, reactFlowNodes, theme.palette.background.default],
  );

  const manualReadyNodeIds = useMemo(
    () => findReadyNodeIds(manualPlan, manualComputedNodeIds),
    [manualComputedNodeIds, manualPlan],
  );

  // Resolved to names here rather than in the panel: the plan snapshots the
  // names it was built with, and a rename doesn't rebuild it
  const manualBlockingNodeNames = useMemo((): string[] => {
    if (manualSelectedNodeId === null) {
      return [];
    }
    return findBlockingNodeIds(
      manualPlan,
      manualSelectedNodeId,
      manualComputedNodeIds,
    ).map((nodeId) => coreGraphAdapter.getNodeNameById(nodeId));
  }, [
    coreGraphAdapter,
    manualComputedNodeIds,
    manualPlan,
    manualSelectedNodeId,
  ]);

  /**
   * Repaints the canvas for a state of the manual run.
   *
   * Everything is derived from (plan, computed, selected) instead of being
   * patched in place, for the same reason the step run recomputes from its
   * index: computing a node changes what several other nodes are allowed to
   * do, and rederiving is the only way that stays right.
   *
   * @param plan The plan of the run.
   * @param computedNodeIds Node IDs already worked out.
   * @param selectedNodeId Node ID open in the panel, or null.
   */
  const applyManualState = useCallback(
    (
      plan: ManualDerivativePlan,
      computedNodeIds: ReadonlySet<string>,
      selectedNodeId: string | null,
    ) => {
      const readyNodeIds = findReadyNodeIds(plan, computedNodeIds);

      setReactFlowNodes((nodes) => {
        // Rebuilt from every value being hidden rather than revealed one at a
        // time as they are worked out. That way turning the answers off puts
        // them all back behind their placeholders, and the setting reaches the
        // canvas instead of only the panel: the numbers on the nodes are the
        // same answers, and hiding them in one place while they sit readable a
        // few pixels away in the other would be no use to anyone
        let newNodes = setManualModeOnNodes(true, nodes);
        if (areAnswersShown) {
          computedNodeIds.forEach((nodeId) => {
            newNodes = revealStepOutput(nodeId, "derivative", newNodes);
          });
        }
        return updateManualNodeStates(
          selectedNodeId,
          computedNodeIds,
          readyNodeIds,
          newNodes,
        );
      });

      setReactFlowEdges((edges) => {
        if (selectedNodeId === null) {
          return clearStepEdgeHighlights(edges);
        }
        // The gradient of a node comes from its output nodes in reverse mode
        // and from its input nodes in forward mode, which is also the side the
        // prerequisites sit on
        const direction: StepFlowDirection = isReverseMode
          ? "outgoing"
          : "incoming";
        return highlightStepEdges(
          selectedNodeId,
          direction,
          "derivative",
          new Set(coreGraphAdapter.getDerivativePathNodeIds()),
          edges,
        );
      });
    },
    [areAnswersShown, coreGraphAdapter, isReverseMode],
  );

  /**
   * Starts the manual run from scratch, or restarts it because the graph
   * changed under it.
   *
   * An edit can move a node onto or off the differentiation path, so the plan
   * is rebuilt and the progress dropped rather than reconciled: a half-run
   * against a graph that no longer matches would teach the wrong thing.
   */
  const restartManualRun = useCallback(() => {
    const plan = coreGraphAdapter.buildManualDerivativePlan();
    const computedNodeIds = new Set<string>();
    setManualPlan(plan);
    setManualComputedNodeIds(computedNodeIds);
    setManualSelectedNodeId(null);
    setManualExplainData(null);
    applyManualState(plan, computedNodeIds, null);
  }, [applyManualState, coreGraphAdapter]);

  const teardownManualRun = useCallback(() => {
    setManualPlan(emptyManualDerivativePlan);
    setManualComputedNodeIds(new Set());
    setManualSelectedNodeId(null);
    setManualExplainData(null);
    setReactFlowNodes((nodes) =>
      deselectAllNodes(setManualModeOnNodes(false, nodes)),
    );
    setReactFlowEdges((edges) => clearStepEdgeHighlights(edges));
  }, []);

  const handleResetManualRun = useCallback(() => {
    const computedNodeIds = new Set<string>();
    setManualComputedNodeIds(computedNodeIds);
    setManualSelectedNodeId(null);
    setManualExplainData(null);
    applyManualState(manualPlan, computedNodeIds, null);
  }, [applyManualState, manualPlan]);

  /**
   * Opens a node in the manual panel.
   *
   * A node that isn't ready yet is opened too. Being told which neighbors are
   * missing is the answer to "why not this one", and refusing the click
   * silently would leave the user guessing.
   */
  const selectManualNode = useCallback(
    (nodeId: string) => {
      const isInPlan = manualPlan.nodes.some((node) => node.nodeId === nodeId);
      if (!isInPlan) {
        const node = reactFlowNodes.find((node) => node.id === nodeId);
        const isConstant =
          node !== undefined &&
          (node.data as NodeData).featureNodeType.nodeType === "constant";
        setErrorMessage(
          isConstant
            ? "The derivative of a constant is 0 by definition, so there's nothing to work out here."
            : "This node isn't on the differentiation path, so no gradient flows through it and its derivative stays 0.",
        );
        setSnackbarOpen(true);
        return;
      }

      setManualSelectedNodeId(nodeId);
      setManualExplainData(
        coreGraphAdapter.buildExplainDerivativeDataForNode(nodeId),
      );
      applyManualState(manualPlan, manualComputedNodeIds, nodeId);
    },
    [
      applyManualState,
      coreGraphAdapter,
      manualComputedNodeIds,
      manualPlan,
      reactFlowNodes,
    ],
  );

  /**
   * Reveals the derivative of the node open in the panel.
   *
   * The core graph has held the value all along; what's manual is the order
   * and the moment, not the arithmetic. So this only lifts the placeholder and
   * records that the node counts as worked out.
   */
  const handleComputeManualDerivative = useCallback(() => {
    if (manualSelectedNodeId === null) {
      return;
    }
    if (
      manualComputedNodeIds.has(manualSelectedNodeId) ||
      !manualReadyNodeIds.has(manualSelectedNodeId)
    ) {
      return;
    }

    const computedNodeIds = new Set(manualComputedNodeIds);
    computedNodeIds.add(manualSelectedNodeId);
    setManualComputedNodeIds(computedNodeIds);
    applyManualState(manualPlan, computedNodeIds, manualSelectedNodeId);
  }, [
    applyManualState,
    manualComputedNodeIds,
    manualPlan,
    manualReadyNodeIds,
    manualSelectedNodeId,
  ]);

  const teardownStepRun = useCallback(() => {
    setPlaying(false);
    setStepPlan([]);
    setStepIndex(0);
    setReactFlowNodes((nodes) =>
      deselectAllNodes(setStepModeOnNodes(false, nodes)),
    );
    setReactFlowEdges((edges) => clearStepEdgeHighlights(edges));
  }, []);

  const setupStepRun = useCallback(() => {
    setPlaying(false);
    const plan = coreGraphAdapter.buildStepPlan();
    setStepPlan(plan);
    applyStepIndex(plan, 0);
  }, [applyStepIndex, coreGraphAdapter]);

  /**
   * Opens, closes or switches the tab of the derivatives inspector.
   *
   * The tabs are not only views: two of them hold the outputs of the canvas
   * back, and in different ways. So the canvas is handed back before the next
   * tab takes it over, and that is what keeps the two walkthroughs from ever
   * running together: a tab bar can only have one tab in front, so nothing has
   * to check.
   *
   * @param nextIsOpen Whether the inspector should be open afterwards.
   * @param nextTab The tab to show.
   */
  const switchInspector = useCallback(
    (nextIsOpen: boolean, nextTab: InspectorTab) => {
      if (isStepMode) {
        teardownStepRun();
      }
      if (isManualMode) {
        teardownManualRun();
      }

      setInspectorOpen(nextIsOpen);
      setInspectorTab(nextTab);

      if (!nextIsOpen) {
        return;
      }
      if (nextTab === "step") {
        setupStepRun();
      } else if (nextTab === "manual") {
        restartManualRun();
      }
    },
    [
      isManualMode,
      isStepMode,
      restartManualRun,
      setupStepRun,
      teardownManualRun,
      teardownStepRun,
    ],
  );

  const handleToggleInspector = useCallback(() => {
    switchInspector(!isInspectorOpen, inspectorTab);
  }, [inspectorTab, isInspectorOpen, switchInspector]);

  const handleInspectorTabChange = useCallback(
    (tabId: InspectorTab) => {
      switchInspector(true, tabId);
    },
    [switchInspector],
  );

  const handleCloseInspector = useCallback(() => {
    switchInspector(false, inspectorTab);
  }, [inspectorTab, switchInspector]);

  /**
   * Restarts the run because the graph changed under it.
   *
   * Called whenever the core graph re-evaluates, so any edit (adding a node,
   * connecting an edge, typing a value, switching the differentiation mode)
   * puts the walkthrough back to the beginning with a fresh plan.
   */
  const restartStepRun = useCallback(() => {
    const plan = coreGraphAdapter.buildStepPlan();
    setStepPlan(plan);
    setPlaying(false);
    applyStepIndex(plan, 0);
  }, [applyStepIndex, coreGraphAdapter]);

  const updateNodeDarkMode = useCallback(() => {
    setReactFlowNodes((nodes) =>
      updateReactFlowNodeDarkMode(isDarkMode, nodes),
    );
  }, [isDarkMode]);

  const handleNameChange = useCallback(
    (nodeId: string, name: string): void => {
      coreGraphAdapter.updateNodeNameById(nodeId, name);
    },
    [coreGraphAdapter],
  );

  const handleInputChange = useCallback(
    (nodeId: string, inputPortId: string, value: string): void => {
      coreGraphAdapter.updateNodeValueById(nodeId, inputPortId, value);

      setReactFlowNodes((nodes) =>
        updateReactFlowNodeInputValue(nodeId, inputPortId, value, nodes),
      );
    },
    [coreGraphAdapter],
  );

  const handleBodyClick = useCallback((nodeId: string): void => {
    setReactFlowNodes((nodes) => selectReactFlowNode(nodeId, nodes));
  }, []);

  const handleDerivativeClick = useCallback(
    (nodeId: string): void => {
      switchInspector(true, "explain");

      setReactFlowNodes((nodes) => selectReactFlowNode(nodeId, nodes));
    },
    [switchInspector],
  );

  /**
   * Collapses or expands one node.
   *
   * Purely visual: the core graph isn't told about it, so the values, the
   * derivatives and the step plan all stay exactly as they were.
   */
  const handleToggleCollapse = useCallback((nodeId: string): void => {
    setReactFlowNodes((nodes) => toggleNodeCollapse(nodeId, nodes));
  }, []);

  // Drives which of the two canvas menu items are worth offering. Both end up
  // disabled on an empty canvas, since 0 is neither below nor above 0
  const collapsedNodeCount = reactFlowNodes.filter(
    (node) => (node.data as NodeData).isCollapsed === true,
  ).length;

  const handleCollapseAll = useCallback(() => {
    setReactFlowNodes((nodes) => setCollapsedOnNodes(true, nodes));
  }, []);

  const handleExpandAll = useCallback(() => {
    setReactFlowNodes((nodes) => setCollapsedOnNodes(false, nodes));
  }, []);

  /**
   * Rearranges the nodes into left-to-right layers.
   *
   * The viewport is fitted afterwards because a layout can move the graph well
   * outside what's currently on screen, and a tidy graph the user can't see
   * looks the same as nothing having happened.
   */
  const handleAutoLayout = useCallback(() => {
    setReactFlowNodes((nodes) =>
      layoutGraph(nodes, reactFlowEdges, isSnapToGrid ? gridGap : null),
    );
    reactFlowInstance?.fitView({ duration: 300, padding: 0.2 });
  }, [isSnapToGrid, reactFlowEdges, reactFlowInstance]);

  /**
   * Whether a node is currently collapsed, so that the context menu can offer
   * the action it doesn't have yet.
   */
  const isNodeCollapsed = useCallback(
    (nodeId: string | null): boolean => {
      if (nodeId === null) {
        return false;
      }
      const node = reactFlowNodes.find((node) => node.id === nodeId);
      if (node === undefined) {
        return false;
      }
      return (node.data as NodeData).isCollapsed === true;
    },
    [reactFlowNodes],
  );

  const addNode = useCallback(
    (featureNodeType: FeatureNodeType, position: XYPosition | null) => {
      const featureOperation = findFeatureOperation(
        featureNodeType,
        featureOperations,
      );

      const nodeId = `${nextNodeId}`;
      const nodeName = nodeNameBuilder.buildName(
        featureNodeType,
        featureOperation,
      );

      // Add the core node
      coreGraphAdapter.addNode(
        featureNodeType,
        featureOperation,
        nodeId,
        nodeName,
      );

      // Add the ReactFlow node
      // Read the order after the core node is in, so the new node gets its own
      // badge right away instead of waiting for the next f() update
      const topologicalOrderMap = coreGraphAdapter.getTopologicalOrderMap();
      const initialOutputValue = coreGraphAdapter.getNodeValueById(nodeId);
      const derivativeTargetName =
        derivativeTarget === null
          ? null
          : coreGraphAdapter.getNodeNameById(derivativeTarget);
      const addNodeData: AddNodeData = {
        featureNodeType,
        featureOperation,
        nodeId,
        nodeName,
        initialOutputValue,
        isReverseMode,
        derivativeTargetName,
        onNameChange: handleNameChange,
        onInputChange: handleInputChange,
        onBodyClick: handleBodyClick,
        onDerivativeClick: handleDerivativeClick,
        onToggleCollapse: handleToggleCollapse,
        isDarkMode,
        decimalPlaces,
        isNodeValuesVisible,
        isNodeDerivativesVisible,
        topologicalOrder: topologicalOrderMap.get(nodeId) ?? null,
        isTopologicalOrderVisible,
      };
      setReactFlowNodes((nodes) => {
        nodes = deselectAllNodes(nodes);
        const finalPosition =
          position ?? getNewReactFlowNodePosition(nodes, lastSelectedNodeId);
        const newNodes = addReactFlowNode(addNodeData, finalPosition, nodes);
        // The existing nodes can be renumbered by the new one, so refresh them
        // all rather than only the node being added
        return updateReactFlowNodeTopologicalOrder(
          topologicalOrderMap,
          isTopologicalOrderVisible,
          newNodes,
        );
      });

      // Add the operation ID to the set for operation nodes
      if (featureOperation !== null) {
        setOperationIdsAddedAtLeastOnce(
          (oldOperationIds) =>
            new Set([...oldOperationIds, featureOperation.id]),
        );
      }

      setNextNodeId((nextNodeId) => nextNodeId + 1);
    },
    [
      coreGraphAdapter,
      derivativeTarget,
      featureOperations,
      handleBodyClick,
      handleDerivativeClick,
      handleInputChange,
      handleNameChange,
      handleToggleCollapse,
      decimalPlaces,
      isDarkMode,
      isNodeValuesVisible,
      isNodeDerivativesVisible,
      isTopologicalOrderVisible,
      isReverseMode,
      lastSelectedNodeId,
      nextNodeId,
      nodeNameBuilder,
    ],
  );

  const handleAddNode = useCallback(
    (featureNodeType: FeatureNodeType) => {
      addNode(featureNodeType, null);
    },
    [addNode],
  );

  const handleAddOperation = useCallback(() => {
    const id = `${nextOperationId}`;

    const newFeatureOperation: FeatureOperation = {
      id,
      name: `Operation ${nextOperationId + 1}`,
      type: "custom",
      namePrefix: "f",
      operation: new Operation(TEMPLATE_F_CODE, TEMPLATE_DFDX_CODE),
      inputPorts: [],
      helpText: "What does this operation do",
    };

    setFeatureOperations((featureOperations) =>
      featureOperations.concat(newFeatureOperation),
    );
    setNextOperationId((nextOperationId) => nextOperationId + 1);
  }, [nextOperationId]);

  const handleEditOperation = useCallback(
    (updatedOperation: FeatureOperation) => {
      setFeatureOperations((featureOperations) => {
        return featureOperations.map((featureOperation) => {
          if (featureOperation.id !== updatedOperation.id) {
            return featureOperation;
          }

          featureOperation.operation.setFCode(
            updatedOperation.operation.getFCode(),
          );
          featureOperation.operation.setDfdxCode(
            updatedOperation.operation.getDfdxCode(),
          );
          return {
            ...updatedOperation,
            operation: featureOperation.operation,
          };
        });
      });

      coreGraphAdapter.updateOutputs();
    },
    [coreGraphAdapter],
  );

  const handleDeleteOperation = useCallback((operationId: string) => {
    setFeatureOperations((featureOperations) => {
      return featureOperations.filter((featureOperation) => {
        return featureOperation.id !== operationId;
      });
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setReactFlowNodes((nodes) => deselectAllNodes(nodes));
  }, []);

  const handleSelectAll = useCallback(() => {
    setReactFlowNodes((nodes) => {
      const allSelectedNodes = selectAllNodes(nodes);
      const selectedNodeIds = allSelectedNodes.map((node) => node.id);
      coreGraphAdapter.updateSelectedNodeIds(selectedNodeIds);
      return updateReactFlowNodeHighlighted(derivativeTarget, allSelectedNodes);
    });

    if (!isStepMode && !isManualMode) {
      setReactFlowEdges((edges) => {
        const selectedNodeIds = reactFlowNodes.map((node) => node.id);
        return updateEdgeAnimations(
          isExplaining,
          isReverseMode,
          derivativeTarget !== null,
          selectedNodeIds,
          new Set(coreGraphAdapter.getDerivativePathNodeIds()),
          edges,
        );
      });
    }
  }, [
    coreGraphAdapter,
    derivativeTarget,
    isExplaining,
    isManualMode,
    isReverseMode,
    isStepMode,
    reactFlowNodes,
  ]);

  const handleSelectNode = useCallback(
    (nodeId: string) => {
      const visibleNodeId = coreGraphAdapter.getVisibleNodeIdById(nodeId);

      setReactFlowNodes((nodes) => {
        const deselectedNodes = deselectAllNodes(nodes);
        return selectReactFlowNode(visibleNodeId, deselectedNodes);
      });
    },
    [coreGraphAdapter],
  );

  const saveFeatureOperations = useCallback((): FeatureOperationState[] => {
    return featureOperations.map((featureOperation) => {
      const operationState: CoreOperationState = {
        fCode: featureOperation.operation.getFCode(),
        dfdxCode: featureOperation.operation.getDfdxCode(),
      };
      const inputPortStates: PortState[] = featureOperation.inputPorts.map(
        (inputPort) => {
          return {
            id: inputPort.getId(),
            allowMultiEdges: inputPort.isAllowMultiEdges(),
          };
        },
      );
      return {
        id: featureOperation.id,
        name: featureOperation.name,
        type: featureOperation.type,
        namePrefix: featureOperation.namePrefix,
        operation: operationState,
        inputPorts: inputPortStates,
        helpText: featureOperation.helpText,
      };
    });
  }, [featureOperations]);

  const handleSave = useCallback((): GraphContainerState => {
    if (reactFlowInstance === null) {
      throw new Error("React flow instance should not be null");
    }

    const coreGraphAdapterState = coreGraphAdapter.save();
    const nodeNameBuilderState = nodeNameBuilder.save();
    return {
      coreGraphAdapterState,
      isReverseMode,
      derivativeTarget,
      featureOperations: saveFeatureOperations(),
      nextNodeId,
      nodeNameBuilderState,
      nextOperationId,
      operationIdsAddedAtLeastOnce: Array.from(operationIdsAddedAtLeastOnce),
      reactFlowState: reactFlowInstance.toObject(),
    };
  }, [
    coreGraphAdapter,
    derivativeTarget,
    isReverseMode,
    nextNodeId,
    nextOperationId,
    nodeNameBuilder,
    operationIdsAddedAtLeastOnce,
    reactFlowInstance,
    saveFeatureOperations,
  ]);

  const loadFeatureOperations = useCallback(
    (featureOperationStates: FeatureOperationState[]): FeatureOperation[] => {
      return featureOperationStates.map((featureOperationState) => {
        return {
          id: featureOperationState.id,
          name: featureOperationState.name,
          type: featureOperationState.type,
          namePrefix: featureOperationState.namePrefix,
          operation: new Operation(
            featureOperationState.operation.fCode,
            featureOperationState.operation.dfdxCode,
          ),
          inputPorts: featureOperationState.inputPorts.map(
            (inputPortState) =>
              new Port(inputPortState.id, inputPortState.allowMultiEdges),
          ),
          helpText: featureOperationState.helpText,
        };
      });
    },
    [],
  );

  const loadReactFlowNode = useCallback(
    (nodes: Node[]) => {
      return nodes.map((node) => {
        const data = node.data as NodeData;
        // Set the new data to notify React Flow about the change
        const newData: NodeData = {
          ...data,
          // Set the callbacks because the json file doesn't have these
          onNameChange: handleNameChange,
          onInputChange: handleInputChange,
          onBodyClick: handleBodyClick,
          onDerivativeClick: handleDerivativeClick,
          onToggleCollapse: handleToggleCollapse,
          // View preferences, so they follow the app rather than the file. A
          // graph saved in light mode carries `isDarkMode: false`, which would
          // otherwise leave the loaded nodes styled for the wrong theme
          decimalPlaces,
          isDarkMode,
          isNodeValuesVisible,
          isNodeDerivativesVisible,
          topologicalOrder: null,
          isTopologicalOrderVisible,
          // A file saved mid-walkthrough carries whatever the nodes were
          // doing at the time, and none of it applies to a fresh load
          walkthroughState: idleWalkthroughState,
          outputItems: data.outputItems.map((outputItem) => ({
            ...outputItem,
            isHidden: false,
          })),
        };

        node.data = newData;
        return node;
      });
    },
    [
      handleBodyClick,
      handleDerivativeClick,
      handleInputChange,
      handleNameChange,
      handleToggleCollapse,
      decimalPlaces,
      isDarkMode,
      isNodeValuesVisible,
      isNodeDerivativesVisible,
      isTopologicalOrderVisible,
    ],
  );

  const loadReactFlow = useCallback(
    (reactFlowState: any) => {
      // Reference: https://reactflow.dev/examples/interaction/save-and-restore
      const { x = 0, y = 0, zoom = 1 } = reactFlowState.viewport;
      const edgesWithMarkers = addEdgeMarkers(reactFlowState.edges);
      setReactFlowNodes(loadReactFlowNode(reactFlowState.nodes));
      setReactFlowEdges(
        updateEdgeLabels(
          edgesWithMarkers,
          coreGraphAdapter,
          isLocalDerivativesVisible,
          decimalPlaces,
          isDarkMode,
        ),
      );
      reactFlowInstance?.setViewport({ x, y, zoom });
    },
    [
      coreGraphAdapter,
      decimalPlaces,
      isDarkMode,
      isLocalDerivativesVisible,
      loadReactFlowNode,
      reactFlowInstance,
    ],
  );

  const handleLoad = useCallback(
    (graphContainerState: GraphContainerState) => {
      const loadedFeatureOperations = loadFeatureOperations(
        graphContainerState.featureOperations,
      );

      coreGraphAdapter.load(
        graphContainerState.coreGraphAdapterState,
        loadedFeatureOperations,
      );
      setReverseMode(graphContainerState.isReverseMode);
      setDerivativeTarget(graphContainerState.derivativeTarget);
      setFeatureOperations(loadedFeatureOperations);
      setNextNodeId(graphContainerState.nextNodeId);
      nodeNameBuilder.load(graphContainerState.nodeNameBuilderState);
      setNextOperationId(graphContainerState.nextOperationId);
      setOperationIdsAddedAtLeastOnce(
        new Set(graphContainerState.operationIdsAddedAtLeastOnce),
      );
      loadReactFlow(graphContainerState.reactFlowState);

      // The file carries the f() and derivative values it was saved with, and
      // nothing guarantees they still match: a graph saved with a different
      // derivative target, or an example file written by hand, brings numbers
      // that were never true of this graph. Recomputing is cheap and makes the
      // first thing the user sees the truth
      coreGraphAdapter.updateOutputs();
    },
    [coreGraphAdapter, loadFeatureOperations, loadReactFlow, nodeNameBuilder],
  );

  const handleNewGraph = useCallback(() => {
    coreGraphAdapter.load(
      {
        coreGraphState: {
          nodeIdToNodes: {},
          differentiationMode: "REVERSE",
          targetNodeId: null,
        },
        nodeIdToNames: {},
        dummyInputNodeIdToNodeIds: {},
      },
      featureOperations,
    );
    setReverseMode(true);
    setDerivativeTarget(null);
    setNextNodeId(0);
    nodeNameBuilder.load({
      constantCounter: 1,
      variableCounter: 1,
      operationIdToCounter: {},
    });
    setNextOperationId(0);
    setOperationIdsAddedAtLeastOnce(new Set());
    setReactFlowNodes([]);
    setReactFlowEdges([]);
    setLastSelectedNodeId(null);
    setExplainDerivativeData([]);
    setManualExplainData(null);
    setManualPlan(emptyManualDerivativePlan);
    setStepPlan([]);
    setStepIndex(0);
    reactFlowInstance?.setViewport({ x: 0, y: 0, zoom: 1 });
  }, [coreGraphAdapter, featureOperations, nodeNameBuilder, reactFlowInstance]);

  const handleReverseModeChange = useCallback(
    (isReverseMode: boolean) => {
      coreGraphAdapter.setDifferentiationMode(
        isReverseMode ? "REVERSE" : "FORWARD",
      );

      setReverseMode(() => isReverseMode);
    },
    [coreGraphAdapter],
  );

  const handleDerivativeTargetChange = useCallback(
    (nodeId: string | null) => {
      coreGraphAdapter.setTargetNode(nodeId);

      setDerivativeTarget(() => nodeId);
    },
    [coreGraphAdapter],
  );

  const handleReactFlowInit: OnInit = useCallback((reactFlowInstance) => {
    setReactFlowInstance(reactFlowInstance);
  }, []);

  const handleNodesChange: OnNodesChange = useCallback(
    (changes) => {
      coreGraphAdapter.changeNodes(changes);

      setReactFlowNodes((nodes) => applyNodeChanges(changes, nodes));
    },
    [coreGraphAdapter],
  );

  const handleEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      coreGraphAdapter.changeEdges(changes, reactFlowEdges);

      setReactFlowEdges((edges) => applyEdgeChanges(changes, edges));
    },
    [coreGraphAdapter, reactFlowEdges],
  );

  const handleSelectionChange = useCallback(
    (params: OnSelectionChangeParams): void => {
      setLastSelectedNodeId((oldLastSelectedNodeId) => {
        const firstSelectedNodeId = findFirstSelectedNodeId(params);
        if (firstSelectedNodeId !== null) {
          return firstSelectedNodeId;
        }
        return oldLastSelectedNodeId;
      });

      const selectedNodeIds = params.nodes.map((node) => node.id);

      coreGraphAdapter.updateSelectedNodeIds(selectedNodeIds);

      setReactFlowNodes((nodes) =>
        updateReactFlowNodeHighlighted(derivativeTarget, nodes),
      );

      // The step and manual highlights own the edges while a run is on
      if (!isStepMode && !isManualMode) {
        setReactFlowEdges((edges) =>
          updateEdgeAnimations(
            isExplaining,
            isReverseMode,
            derivativeTarget !== null,
            selectedNodeIds,
            new Set(coreGraphAdapter.getDerivativePathNodeIds()),
            edges,
            isGlobalNodeInfluence,
          ),
        );
      }
    },
    [
      coreGraphAdapter,
      derivativeTarget,
      isExplaining,
      isGlobalNodeInfluence,
      isManualMode,
      isReverseMode,
      isStepMode,
    ],
  );

  const handleConnect: OnConnect = useCallback(
    (connection: Connection) => {
      coreGraphAdapter.addConnection(connection);
    },
    [coreGraphAdapter],
  );

  const handleDropNode = useCallback(
    (featureNodeType: FeatureNodeType, position: XYPosition) => {
      addNode(featureNodeType, position);
    },
    [addNode],
  );

  /**
   * Turns a click on the canvas into a pick in the manual run.
   *
   * Only that mode listens: everywhere else a click already means selection,
   * and giving it a second meaning would make the canvas unpredictable.
   */
  const handleNodeClick: NodeMouseHandler = useCallback(
    (event, node) => {
      if (!isManualMode) {
        return;
      }
      selectManualNode(node.id);
    },
    [isManualMode, selectManualNode],
  );

  const handleNodeContextMenu: NodeMouseHandler = useCallback(
    (event, node) => {
      // Replace the browser menu with ours
      event.preventDefault();

      setContextMenuAnchor({
        nodeId: node.id,
        nodeName: coreGraphAdapter.getNodeNameById(node.id),
        top: event.clientY,
        left: event.clientX,
      });
    },
    [coreGraphAdapter],
  );

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuAnchor(null);
  }, []);

  const handlePaneContextMenu = useCallback((event: ReactMouseEvent) => {
    // Replace the browser menu with ours
    event.preventDefault();

    setPaneContextMenuAnchor({
      top: event.clientY,
      left: event.clientX,
    });
  }, []);

  const handleClosePaneContextMenu = useCallback(() => {
    setPaneContextMenuAnchor(null);
  }, []);

  /**
   * Adds another node of the same type next to the one right clicked.
   *
   * Only the type is copied: the values belong to the original node, and
   * duplicating them would silently create a second source of truth the user
   * didn't ask for.
   */
  const handleDuplicateNode = useCallback(
    (nodeId: string) => {
      const node = reactFlowNodes.find((node) => node.id === nodeId);
      if (node === undefined) {
        return;
      }
      const data = node.data as NodeData;
      addNode(data.featureNodeType, {
        x: node.position.x + 40,
        y: node.position.y + 40,
      });
    },
    [addNode, reactFlowNodes],
  );

  /**
   * Removes a node together with the edges hanging off it.
   *
   * The edges have to go first: removing a core node already drops its
   * connections, so disconnecting afterwards would look up a node that no
   * longer exists.
   */
  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      const node = reactFlowNodes.find((node) => node.id === nodeId);
      if (node === undefined) {
        return;
      }

      const connectedEdges = getConnectedEdges([node], reactFlowEdges);
      if (connectedEdges.length > 0) {
        handleEdgesChange(
          connectedEdges.map((edge) => ({ id: edge.id, type: "remove" })),
        );
      }

      handleNodesChange([{ id: nodeId, type: "remove" }]);
    },
    [handleEdgesChange, handleNodesChange, reactFlowEdges, reactFlowNodes],
  );

  const handleSnackbarClose = useCallback(
    (event?: SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }

      setSnackbarOpen(false);
    },
    [],
  );

  // Listen to core graph adapter events
  useEffect(() => {
    coreGraphAdapter.onConnectionAdded(handleConnectionAdded);
    coreGraphAdapter.onConnectionError(handleConnectionError);
    coreGraphAdapter.onTargetNodeUpdated(handleTargetNodeUpdated);
    coreGraphAdapter.onNodeNameUpdated(handleNodeNameUpdated);
    coreGraphAdapter.onShowInputFields(handleShowInputFields);
    coreGraphAdapter.onHideInputField(handleHideInputField);
    coreGraphAdapter.onFValuesUpdated(handleFValuesUpdated);
    coreGraphAdapter.onDerivativeValuesUpdated(handleDerivativeValuesUpdated);
    coreGraphAdapter.onExplainDerivativeDataUpdated(
      handleExplainDerivativeDataUpdated,
    );

    return () => {
      coreGraphAdapter.removeAllCallbacks();
    };
  });

  const handleConnectionAdded = useCallback(
    (connection: Connection) => {
      setReactFlowEdges((edges) => {
        const edgesWithNewConn = addEdge(
          { ...connection, markerEnd: buildEdgeMarker() },
          edges,
        );
        return updateEdgeLabels(
          edgesWithNewConn,
          coreGraphAdapter,
          isLocalDerivativesVisible,
          decimalPlaces,
          isDarkMode,
        );
      });
    },
    [coreGraphAdapter, decimalPlaces, isDarkMode, isLocalDerivativesVisible],
  );

  const handleConnectionError = useCallback((error: Error) => {
    setErrorMessage(error.message);
    setSnackbarOpen(true);
  }, []);

  const handleTargetNodeUpdated = useCallback((targetNodeId: string | null) => {
    setDerivativeTarget(() => targetNodeId);
  }, []);

  const handleNodeNameUpdated = useCallback((nodeId: string, name: string) => {
    setReactFlowNodes((nodes) => updateReactFlowNodeName(nodeId, name, nodes));
  }, []);

  const handleShowInputFields = useCallback((emptyPortEdges: Edge[]) => {
    setReactFlowNodes((nodes) => showInputFields(emptyPortEdges, nodes));
  }, []);

  const handleHideInputField = useCallback(
    (nonEmptyPortConnection: Connection) => {
      setReactFlowNodes((nodes) =>
        hideInputField(nonEmptyPortConnection, nodes),
      );
    },
    [],
  );

  const handleFValuesUpdated = useCallback(
    (nodeIdToFValues: ReadonlyMap<string, string>) => {
      const topoMap = coreGraphAdapter.getTopologicalOrderMap();
      setReactFlowNodes((nodes) => {
        const nodesWithF = updateReactFlowNodeFValues(nodeIdToFValues, nodes);
        return updateReactFlowNodeTopologicalOrder(
          topoMap,
          isTopologicalOrderVisible,
          nodesWithF,
        );
      });
      setReactFlowEdges((edges) =>
        updateEdgeLabels(
          edges,
          coreGraphAdapter,
          isLocalDerivativesVisible,
          decimalPlaces,
          isDarkMode,
        ),
      );

      // The core graph re-evaluated, so the walkthrough is stale
      if (isStepMode) {
        restartStepRun();
      }
      if (isManualMode) {
        restartManualRun();
      }
    },
    [
      coreGraphAdapter,
      decimalPlaces,
      isDarkMode,
      isLocalDerivativesVisible,
      isManualMode,
      isStepMode,
      isTopologicalOrderVisible,
      restartManualRun,
      restartStepRun,
    ],
  );

  const handleDerivativeValuesUpdated = useCallback(
    (
      differentiationMode: DifferentiationMode,
      targetNodeName: string | null,
      nodeIdToDerivatives: ReadonlyMap<string, string>,
      nodeIdToNames: ReadonlyMap<string, string>,
    ) => {
      setReactFlowNodes((nodes) =>
        updateReactFlowNodeDerivatives(
          nodeIdToDerivatives,
          nodeIdToNames,
          differentiationMode === "REVERSE",
          targetNodeName,
          nodes,
        ),
      );
    },
    [],
  );

  const handleExplainDerivativeDataUpdated = useCallback(
    (data: ExplainDerivativeData[]): void => {
      setExplainDerivativeData(() => data);
    },
    [],
  );

  // Update node data whenever dark mode changes
  useEffect(() => {
    updateNodeDarkMode();
  }, [isDarkMode, updateNodeDarkMode]);

  // Update edge labels whenever dark mode, decimal places, or local derivative visibility changes
  useEffect(() => {
    setReactFlowEdges((edges) =>
      updateEdgeLabels(
        edges,
        coreGraphAdapter,
        isLocalDerivativesVisible,
        decimalPlaces,
        isDarkMode,
      ),
    );
  }, [coreGraphAdapter, decimalPlaces, isDarkMode, isLocalDerivativesVisible]);

  // Everything that prints a number follows the same setting: the nodes, the
  // explanation panel, and the working the manual mode is showing right now
  useEffect(() => {
    setReactFlowNodes((nodes) =>
      updateReactFlowNodeDecimalPlaces(decimalPlaces, nodes),
    );

    // Rebuilds the explanation panel by way of its event
    coreGraphAdapter.setDecimalPlaces(decimalPlaces);

    // The manual working isn't event-driven: it's built when a node is picked,
    // so the open one has to be rebuilt by hand
    setManualExplainData((manualExplainData) =>
      manualExplainData === null
        ? null
        : coreGraphAdapter.buildExplainDerivativeDataForNode(
            manualExplainData.nodeId,
          ),
    );
  }, [coreGraphAdapter, decimalPlaces]);

  // Update node values and derivatives visibility whenever settings change
  useEffect(() => {
    setReactFlowNodes((nodes) =>
      updateReactFlowNodeValuesVisibility(
        isNodeValuesVisible,
        isNodeDerivativesVisible,
        nodes,
      ),
    );
  }, [isNodeValuesVisible, isNodeDerivativesVisible]);

  /**
   * Makes room for the right column on a window too narrow for both panels.
   *
   * Four columns don't fit under {@link THREE_COLUMN_MIN_WIDTH}: something has
   * to give, and it's the tool panel rather than the canvas, since the canvas
   * is what the right column is talking about. The feature is really
   * deselected rather than just hidden, so the navigator doesn't claim to be
   * showing a panel that isn't there, and it's handed back when the column
   * closes.
   *
   * Only the opening and the closing are acted on. Keeping the panel shut for
   * as long as the column is open would make the navigator item look broken:
   * the user would click it and watch it close again.
   */
  useEffect(() => {
    const wasRightColumnOpen = wasRightColumnOpenRef.current;
    wasRightColumnOpenRef.current = isInspectorOpen;

    if (isInspectorOpen && !wasRightColumnOpen) {
      // Nothing to yield when the column is already collapsed: the panel
      // isn't taking any width, so dropping the selection would cost the user
      // their place for no room in return
      if (
        !isWideEnoughForBothPanels &&
        isSidebarOpen &&
        selectedFeature !== null
      ) {
        yieldedFeatureRef.current = selectedFeature;
        onSelectFeature(null);
      }
      return;
    }

    if (!isInspectorOpen && wasRightColumnOpen) {
      const yieldedFeature = yieldedFeatureRef.current;
      yieldedFeatureRef.current = null;
      // Not if the user picked something in the meantime: that choice is newer
      if (yieldedFeature !== null && selectedFeature === null) {
        onSelectFeature(yieldedFeature);
      }
    }
  }, [
    isInspectorOpen,
    isSidebarOpen,
    isWideEnoughForBothPanels,
    onSelectFeature,
    selectedFeature,
  ]);

  // The setting has to reach a run that is already under way, not just the
  // next one. Re-applying is enough because what the nodes show is worked out
  // from the progress rather than patched into them as it happens
  useEffect(() => {
    if (!isManualMode) {
      return;
    }
    applyManualState(manualPlan, manualComputedNodeIds, manualSelectedNodeId);
  }, [
    applyManualState,
    isManualMode,
    manualComputedNodeIds,
    manualPlan,
    manualSelectedNodeId,
  ]);

  // Opening or closing the right column changes the canvas width without
  // touching what React Flow is showing, so whatever sat against the right edge
  // lands outside the visible area. In manual mode those are nodes the user has
  // to click, and a node that can't be clicked can't be worked out.
  //
  // A layout effect so that the re-render it causes lands before the browser
  // paints: asking from a passive effect would show the clipped graph for a
  // frame first
  useLayoutEffect(() => {
    setFitRequestId((fitRequestId) => fitRequestId + 1);
  }, [isInspectorOpen]);

  // Advance the walkthrough automatically while playing
  useEffect(() => {
    if (!isPlaying) {
      return;
    }
    if (stepIndex >= stepPlan.length) {
      setPlaying(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      applyStepIndex(stepPlan, stepIndex + 1);
    }, 1100);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [applyStepIndex, isPlaying, stepIndex, stepPlan]);

  // Select all nodes shortcut (Ctrl+A / Cmd+A)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === "a" || event.key === "A")
      ) {
        const target = event.target as HTMLElement | null;
        const isEditable =
          target !== null &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.isContentEditable ||
            Boolean(target.closest?.(".monaco-editor")));

        if (isEditable) {
          return;
        }

        event.preventDefault();
        handleSelectAll();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSelectAll]);

  // Which panels the column offers. The dock decides which one is in front,
  // and it keys that off the ids, so rebuilding the content here every render
  // costs nothing
  // All three tabs are always offered. Each says for itself what it needs, and
  // hiding a tab until its precondition is met would hide the feature from
  // anyone who has not met it yet
  const rightPanelTabs: Array<RightPanelTab<InspectorTab>> = [
    {
      id: "step",
      label: "Step",
      content: (
        <StepExecutionPanel
          isPlaying={isPlaying}
          stepIndex={stepIndex}
          steps={stepPlan}
          differentiationMode={isReverseMode ? "REVERSE" : "FORWARD"}
          onStepForward={handleStepForward}
          onStepBackward={handleStepBackward}
          onTogglePlay={handleTogglePlay}
          onReset={handleResetSteps}
        />
      ),
    },
    {
      id: "manual",
      label: "Manual",
      content: (
        <ManualDerivativePanel
          plan={manualPlan}
          computedNodeIds={manualComputedNodeIds}
          readyNodeIds={manualReadyNodeIds}
          selectedNodeId={manualSelectedNodeId}
          explainDerivativeData={manualExplainData}
          blockingNodeNames={manualBlockingNodeNames}
          differentiationMode={isReverseMode ? "REVERSE" : "FORWARD"}
          areAnswersShown={areAnswersShown}
          onCompute={handleComputeManualDerivative}
          onReset={handleResetManualRun}
          onSelectNode={selectManualNode}
        />
      ),
    },
    {
      id: "explain",
      label: "Explain",
      // The panel is a fragment of stacked accordions, so it needs a box of
      // its own to grow into and scroll inside
      content: (
        <Box flexGrow={1} overflow="auto" sx={{ minHeight: 0 }}>
          <ExplainDerivativesPanel
            hasNodes={reactFlowNodes.length > 0}
            hasDerivativeTarget={derivativeTarget !== null}
            explainDerivativeData={explainDerivativeData}
            onClearSelection={handleClearSelection}
            onClickLatexLink={handleSelectNode}
          />
        </Box>
      ),
    },
  ];

  return (
    <>
      {/* Toolbar padding */}
      <Toolbar />

      {/* Title */}
      <Title
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={onToggleSidebar}
        isDarkMode={isDarkMode}
        onToggleDarkMode={onToggleDarkMode}
        settings={
          <SettingsMenu
            isGridVisible={isGridVisible}
            isSnapToGrid={isSnapToGrid}
            isTooltipsVisible={isTooltipsVisible}
            decimalPlaces={decimalPlaces}
            isNodeValuesVisible={isNodeValuesVisible}
            isNodeDerivativesVisible={isNodeDerivativesVisible}
            isLocalDerivativesVisible={isLocalDerivativesVisible}
            isTopologicalOrderVisible={isTopologicalOrderVisible}
            isGlobalNodeInfluence={isGlobalNodeInfluence}
            areAnswersShown={areAnswersShown}
            onToggleGrid={handleToggleGrid}
            onToggleSnapToGrid={handleToggleSnapToGrid}
            onToggleTooltips={onToggleTooltips}
            onDecimalPlacesChange={handleDecimalPlacesChange}
            onToggleNodeValues={handleToggleNodeValues}
            onToggleNodeDerivatives={handleToggleNodeDerivatives}
            onToggleLocalDerivatives={handleToggleLocalDerivatives}
            onToggleTopologicalOrder={handleToggleTopologicalOrder}
            onToggleGlobalNodeInfluence={handleToggleGlobalNodeInfluence}
            onToggleAnswers={handleToggleAnswers}
          />
        }
      >
        <GraphToolbar
          isReverseMode={isReverseMode}
          derivativeTarget={derivativeTarget}
          nodeIds={coreGraphAdapter.getVisibleNodeIds()}
          nodeNames={coreGraphAdapter.getVisibleNodeNames()}
          onReverseModeChange={handleReverseModeChange}
          onDerivativeTargetChange={handleDerivativeTargetChange}
        />

        {/* Sits with the mode and the target because it belongs to the same
        job: they set the differentiation up, this reads it back */}
        <Tooltip title="Walk through and explain the derivatives">
          <Button
            color="inherit"
            size="small"
            startIcon={<SchoolIcon />}
            variant={isInspectorOpen ? "outlined" : "text"}
            onClick={handleToggleInspector}
            aria-label="toggle-derivatives-inspector"
            sx={{ whiteSpace: "nowrap" }}
          >
            Derivatives
          </Button>
        </Tooltip>
      </Title>
      {/* Graph content */}
      <Grid
        container
        direction="row"
        wrap="nowrap"
        sx={{ height: `calc(100vh - ${TITLE_HEIGHT}px)` }}
      >
        {/* Feature panel, part of the left column so it goes with it. One
        click on the collapse then hands the canvas the whole left side rather
        than only the navigator's share of it */}
        {selectedFeature !== null && isSidebarOpen && (
          <Grid item borderRight={1} borderColor="divider">
            <FeaturePanel
              feature={selectedFeature}
              featureOperations={featureOperations}
              operationIdsAddedAtLeastOnce={operationIdsAddedAtLeastOnce}
              isDarkMode={isDarkMode}
              hasNodes={reactFlowNodes.length > 0}
              isExporting={isExporting}
              onAddNode={handleAddNode}
              onAddOperation={handleAddOperation}
              onEditOperation={handleEditOperation}
              onDeleteOperation={handleDeleteOperation}
              onSave={handleSave}
              onLoad={handleLoad}
              onNewGraph={handleNewGraph}
              onExport={handleExport}
            />
          </Grid>
        )}

        <Grid item display="flex" flexGrow={1}>
          <Grid container direction="column" flexGrow={1}>
            {/* Graph mock */}
            {isTest && (
              <Grid item display="flex">
                <ReactFlowGraphTestHelper
                  nodes={reactFlowNodes}
                  edges={reactFlowEdges}
                  onNodesChange={handleNodesChange}
                  onEdgesChange={handleEdgesChange}
                  onSelectionChange={handleSelectionChange}
                  onConnect={handleConnect}
                  onDropNode={handleDropNode}
                  onNodeClick={handleNodeClick}
                  onNodeContextMenu={handleNodeContextMenu}
                />
              </Grid>
            )}

            {/* Graph */}
            <Grid item display="flex" flexGrow={1} position="relative">
              <ReactFlowGraph
                nodes={reactFlowNodes}
                edges={reactFlowEdges}
                onInit={handleReactFlowInit}
                onNodesChange={handleNodesChange}
                onEdgesChange={handleEdgesChange}
                onSelectionChange={handleSelectionChange}
                onConnect={handleConnect}
                onDropNode={handleDropNode}
                onNodeClick={handleNodeClick}
                fitRequestId={fitRequestId}
                onNodeContextMenu={handleNodeContextMenu}
                onPaneContextMenu={handlePaneContextMenu}
                isDarkMode={isDarkMode}
                isGridVisible={isGridVisible}
                isSnapToGrid={isSnapToGrid}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* The derivative panels, docked opposite the feature panel. They're a
        column rather than an overlay because the user keeps clicking the nodes
        while they're open, and a panel this tall would cover them */}
        {isInspectorOpen && (
          <Grid item borderLeft={1} borderColor="divider">
            <RightPanelDock
              tabs={rightPanelTabs}
              activeTabId={inspectorTab}
              onTabChange={handleInspectorTabChange}
              onClose={handleCloseInspector}
            />
          </Grid>
        )}
      </Grid>
      {/* Right-click menu on the canvas */}
      <PaneContextMenu
        anchor={paneContextMenuAnchor}
        canCollapseAll={collapsedNodeCount < reactFlowNodes.length}
        canExpandAll={collapsedNodeCount > 0}
        canLayout={reactFlowNodes.length > 0}
        onClose={handleClosePaneContextMenu}
        onCollapseAll={handleCollapseAll}
        onExpandAll={handleExpandAll}
        onAutoLayout={handleAutoLayout}
      />

      {/* Right-click menu on a node */}
      <NodeContextMenu
        anchor={contextMenuAnchor}
        isDerivativeTarget={
          contextMenuAnchor !== null &&
          contextMenuAnchor.nodeId === derivativeTarget
        }
        isCollapsed={isNodeCollapsed(contextMenuAnchor?.nodeId ?? null)}
        onClose={handleCloseContextMenu}
        onSetDerivativeTarget={handleDerivativeTargetChange}
        onClearDerivativeTarget={() => {
          handleDerivativeTargetChange(null);
        }}
        onExplainDerivative={handleDerivativeClick}
        onToggleCollapse={handleToggleCollapse}
        onDuplicate={handleDuplicateNode}
        onDelete={handleDeleteNode}
      />

      {/* Error message */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={isSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GraphContainer;
